import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Typography, Chip, CircularProgress, Alert,
  TextField, InputAdornment, LinearProgress,
  Button, Card, CardContent, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import {
  Search, ArrowForward, ArrowBack, CheckCircle,
  Visibility, Download, HourglassEmpty,
  Email, Phone, Business, Work, Close,
} from "@mui/icons-material";
import { ThemeProvider } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import recruiterTheme, {
  FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, SHADOW, PAGE_PADDING, CONTROL_HEIGHT, ELLIPSIS, humanizeStatus,
} from "../../../styles/recruiterTheme";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const STATUS_COLORS = {
  "Pending Setup": { bg: COLORS.warningSoft, color: COLORS.warning },
  "Initiated":     { bg: COLORS.infoSoft,    color: COLORS.info },
  "In Progress":   { bg: COLORS.accentSoft,  color: COLORS.primaryDark },
  "Completed":     { bg: COLORS.successSoft, color: COLORS.success },
};

// Shared styling
const cardSx = { bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: `${RADIUS.lg}px`, boxShadow: SHADOW.card };
const btnSx = { height: CONTROL_HEIGHT.md, textTransform: "none", fontWeight: 600, borderRadius: `${RADIUS.sm}px`, px: 2 };
const headSx = {
  fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: COLORS.muted, textTransform: "uppercase",
  letterSpacing: "0.06em", whiteSpace: "nowrap", bgcolor: COLORS.surfaceTint, borderBottom: `1px solid ${COLORS.border}`,
  py: 1.5, px: 2,
};
const cellSx = { py: 1.5, px: 2, borderBottom: `1px solid ${COLORS.border}` };
// Fixed widths (sum ≈ 1056px) so the list fits a 1080px content area.
const COLS = [
  { h: "Candidate", w: 304 }, { h: "Role · Company", w: 256 }, { h: "Docs", w: 80, align: "right" },
  { h: "Onboarding", w: 168 }, { h: "Status", w: 136 }, { h: "", w: 56 },
];

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Reads a JSON body without throwing on HTML / empty error pages.
const readJson = (res) => res.json().catch(() => ({ success: false, message: `Request failed (${res.status})` }));

// Standalone routes (/accepted-candidates, /accepted-candidates/:id) need a logged-in user;
// inside the HR dashboard the host already checked.
export function useRequireUser(standalone) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!standalone) return;
    let user = null;
    try { user = JSON.parse(localStorage.getItem("rbotUser") || "null"); } catch { user = null; }
    if (!user && !localStorage.getItem("token")) navigate("/login", { replace: true });
  }, [standalone, navigate]);
}

// Centered state inside a card (loading / empty / error).
const CardState = ({ children }) => (
  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" textAlign="center" py={8} px={3} gap={2}>
    {children}
  </Box>
);

// ── In-page document preview modal ────────────────────────────────────────

function DocumentPreviewDialog({ open, onClose, doc, employeeId }) {
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [blobUrl, setBlobUrl]   = useState(null);
  const [contentType, setContentType] = useState("");

  useEffect(() => {
    if (!open || !doc) return;

    let currentBlobUrl = null;
    let cancelled = false;
    setLoading(true);
    setError("");
    setBlobUrl(null);
    setContentType("");

    const fileUrl = `${API_BASE}/onboarding/${employeeId}/document/${doc.idx}/file`;

    fetch(fileUrl, { headers: getAuthHeaders() })
      .then(res => {
        if (!res.ok) throw new Error(`Server responded ${res.status}`);
        const ct = res.headers.get("content-type") || "application/octet-stream";
        return res.arrayBuffer().then(buf => ({ buf, ct }));
      })
      .then(({ buf, ct }) => {
        const blob = new Blob([buf], { type: ct });
        currentBlobUrl = URL.createObjectURL(blob);
        if (cancelled) { URL.revokeObjectURL(currentBlobUrl); return; }
        setBlobUrl(currentBlobUrl);
        setContentType(ct);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.error("[preview error]", err);
        setError(`Could not load this document. ${err.message}`);
        setLoading(false);
      });

    // Clean up the blob URL when the dialog closes or the doc changes.
    return () => {
      cancelled = true;
      if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl);
    };
  }, [open, doc, employeeId]);

  const handleDownload = () => {
    if (!blobUrl || !doc) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = doc.file_name || doc.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const isImage = contentType.startsWith("image/");
  const isPdf   = contentType.startsWith("application/pdf");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: `${RADIUS.lg}px`, overflow: "hidden" } }}
    >
      <DialogTitle sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2,
        bgcolor: COLORS.surfaceTint, borderBottom: `1px solid ${COLORS.border}`, py: 1.5,
      }}>
        <Box minWidth={0}>
          <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.cardTitle, color: COLORS.primaryDeep, ...ELLIPSIS }} title={doc?.name}>
            {doc?.name}
          </Typography>
          <Typography sx={{ fontSize: TYPE.caption, color: COLORS.muted, ...ELLIPSIS }} title={doc?.file_name}>
            {doc?.file_name}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close preview">
          <Close sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{
        p: 0, bgcolor: COLORS.background,
        minHeight: 400, display: "flex",
        alignItems: "center", justifyContent: "center",
      }}>
        {loading && (
          <Box display="flex" flexDirection="column" alignItems="center" gap={1.5} py={8}>
            <CircularProgress sx={{ color: COLORS.primary }} />
            <Typography sx={{ fontSize: TYPE.small, color: COLORS.muted }}>Loading document…</Typography>
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {!loading && !error && blobUrl && isImage && (
          <Box sx={{ width: "100%", textAlign: "center", p: 2 }}>
            <img
              src={blobUrl}
              alt={doc?.file_name || doc?.name}
              style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: RADIUS.sm, boxShadow: SHADOW.card }}
            />
          </Box>
        )}

        {!loading && !error && blobUrl && isPdf && (
          <iframe
            src={blobUrl}
            title={doc?.name}
            style={{ width: "100%", height: "75vh", border: "none" }}
          />
        )}

        {!loading && !error && blobUrl && !isImage && !isPdf && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography sx={{ fontSize: TYPE.body, color: COLORS.muted }}>
              This file type can't be previewed in the browser. Please download it to view.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${COLORS.border}`, bgcolor: COLORS.surfaceTint }}>
        <Button onClick={onClose} sx={{ ...btnSx, color: COLORS.muted }}>Close</Button>
        <Button
          variant="contained"
          startIcon={<Download sx={{ fontSize: 16 }} />}
          onClick={handleDownload}
          disabled={!blobUrl}
          sx={{ ...btnSx, bgcolor: COLORS.primary, "&:hover": { bgcolor: COLORS.primaryDark } }}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Single uploaded-document row ──────────────────────────────────────────
function DocumentRow({ doc, onPreview }) {
  return (
    <Box
      sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        p: 1.5, borderRadius: `${RADIUS.md}px`,
        border: `1px solid ${COLORS.border}`,
        bgcolor: doc.has_file ? COLORS.successSoft : COLORS.surfaceTint,
      }}
    >
      <Box sx={{ flexShrink: 0, display: "flex" }}>
        {doc.has_file
          ? <CheckCircle sx={{ fontSize: 20, color: COLORS.success }} />
          : <HourglassEmpty sx={{ fontSize: 20, color: COLORS.faint }} />}
      </Box>

      <Box flex={1} minWidth={0}>
        <Typography sx={{ fontSize: TYPE.small, fontWeight: 600, color: COLORS.ink, ...ELLIPSIS }} title={doc.name}>
          {doc.name || "Untitled document"}
        </Typography>
        <Typography sx={{ fontSize: TYPE.caption, color: COLORS.muted, ...ELLIPSIS }}
          title={[doc.category, doc.file_name].filter(Boolean).join(" · ")}>
          {[doc.category, doc.file_name].filter(Boolean).join(" · ") || "—"}
        </Typography>
      </Box>

      <Chip
        label={humanizeStatus(doc.status || "Pending")}
        size="small"
        sx={{
          bgcolor: doc.has_file ? COLORS.surface : COLORS.border,
          color: doc.has_file ? COLORS.success : COLORS.muted,
        }}
      />

      {/* Opens the in-page preview dialog (no new tab) */}
      {doc.has_file ? (
        <Tooltip title="View document">
          <IconButton size="small" onClick={() => onPreview(doc)} sx={{ color: COLORS.primary }}>
            <Visibility sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      ) : <Box sx={{ width: 30, flexShrink: 0 }} />}
    </Box>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
      {icon}
      <Typography sx={{ fontSize: TYPE.small, color: COLORS.muted, minWidth: 88, flexShrink: 0 }}>{label}</Typography>
      <Typography sx={{ fontSize: TYPE.small, color: COLORS.ink, fontWeight: 600, ...ELLIPSIS }} title={value || ""}>{value || "—"}</Typography>
    </Box>
  );
}

// ── Detail view (rendered inline on the list, and by the /accepted-candidates/:id route) ──
export function CandidateDetailView({ candidateId, onBack }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [previewDoc, setPreviewDoc] = useState(null); // doc being previewed, or null
  const [reloadKey, setReloadKey]   = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetch(`${API_BASE}/onboarding/accepted-candidates/${candidateId}`, {
      headers: getAuthHeaders(),
    })
      .then(readJson)
      .then(res => {
        if (cancelled) return;
        if (!res?.success || !res.data) {
          setError(res?.message || "Could not load candidate details");
        } else {
          setData(res.data);
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Network error while loading candidate details");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [candidateId, reloadKey]);

  const backBtn = (
    <Button startIcon={<ArrowBack />} onClick={onBack}
      sx={{ ...btnSx, px: 1, mb: 2, color: COLORS.muted }}>
      Back to Accepted Candidates
    </Button>
  );

  if (loading) {
    return (
      <Box>
        {backBtn}
        <Card elevation={0} sx={cardSx}>
          <CardState>
            <CircularProgress size={32} sx={{ color: COLORS.primary }} />
            <Typography sx={{ fontSize: TYPE.body, color: COLORS.muted }}>Loading candidate…</Typography>
          </CardState>
        </Card>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        {backBtn}
        <Card elevation={0} sx={cardSx}>
          <CardState>
            <Typography sx={{ fontSize: TYPE.body, color: COLORS.error }}>{error || "Could not load candidate details"}</Typography>
            <Button variant="outlined" onClick={() => setReloadKey(k => k + 1)} sx={btnSx}>Retry</Button>
          </CardState>
        </Card>
      </Box>
    );
  }

  const candidate   = data.candidate || {};
  const employee_id = data.employee_id;
  const documents   = Array.isArray(data.documents) ? data.documents : [];
  const uploadedCount = documents.filter(d => d.has_file).length;
  const allUploaded   = documents.length > 0 && uploadedCount === documents.length;

  return (
    <Box>
      {backBtn}

      <Card elevation={0} sx={{ ...cardSx, mb: 3, overflow: "hidden" }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${COLORS.primaryDeep} 0%, ${COLORS.primary} 100%)`,
          px: 3, py: 3,
        }}>
          <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: TYPE.overline, fontWeight: 700,
            textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>
            Accepted Candidate
          </Typography>
          <Typography sx={{ color: "#fff", fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.section, lineHeight: 1.25 }}>
            {candidate.candidatename || "—"}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: TYPE.body, mt: 0.5 }}>
            {[candidate.jobRole, candidate.companyName].filter(Boolean).join(" · ") || "—"}
          </Typography>
        </Box>

        <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
          <Box display="flex" flexDirection="column" gap={1.5}>
            <InfoRow icon={<Email sx={{ fontSize: 16, color: COLORS.muted }} />} label="Email" value={candidate.candidateEmail} />
            <InfoRow icon={<Phone sx={{ fontSize: 16, color: COLORS.muted }} />} label="Phone" value={candidate.phone} />
            <InfoRow icon={<Work sx={{ fontSize: 16, color: COLORS.muted }} />} label="Role" value={candidate.jobRole} />
            <InfoRow icon={<Business sx={{ fontSize: 16, color: COLORS.muted }} />} label="Company" value={candidate.companyName} />
          </Box>
        </CardContent>
      </Card>

      <Card elevation={0} sx={cardSx}>
        <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} gap={2}>
            <Typography sx={{ fontWeight: 700, fontSize: TYPE.cardTitle, color: COLORS.primaryDeep }}>
              Uploaded Documents
            </Typography>
            <Chip
              label={`${uploadedCount} / ${documents.length} uploaded`}
              size="small"
              sx={{
                bgcolor: allUploaded ? COLORS.successSoft : COLORS.border,
                color: allUploaded ? COLORS.success : COLORS.muted,
                fontVariantNumeric: "tabular-nums",
              }}
            />
          </Box>

          {documents.length === 0 ? (
            <Typography sx={{ fontSize: TYPE.body, color: COLORS.muted, textAlign: "center", py: 4 }}>
              No documents have been uploaded yet.
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={1}>
              {documents.map((doc, i) => (
                <DocumentRow
                  key={doc.idx ?? i}
                  doc={doc}
                  onPreview={setPreviewDoc}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Preview modal — shows the actual original document inline */}
      <DocumentPreviewDialog
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        doc={previewDoc}
        employeeId={employee_id}
      />
    </Box>
  );
}

// Standalone route wrapper: theme + lavender canvas + PAGE_PADDING. Embedded in the HR
// dashboard tab, the host supplies the canvas and padding, so this adds none.
export function AcceptedPageShell({ standalone, children }) {
  return (
    <ThemeProvider theme={recruiterTheme}>
      <Box sx={{
        fontFamily: FONT_BODY, color: COLORS.ink, width: "100%", boxSizing: "border-box",
        ...(standalone ? { bgcolor: COLORS.background, minHeight: "100vh", p: `${PAGE_PADDING}px` } : {}),
      }}>
        {standalone ? <Box sx={{ maxWidth: 1080, mx: "auto" }}>{children}</Box> : children}
      </Box>
    </ThemeProvider>
  );
}

// ── Main export: list + detail toggled by local state ────────────────────────
export default function AcceptedCandidatesPage() {
  const { pathname } = useLocation();
  const standalone = pathname.startsWith("/accepted-candidates");
  useRequireUser(standalone);

  const [candidates, setCandidates]       = useState([]);
  const [loading, setLoading]             = useState(true);
  const [loaded, setLoaded]               = useState(false);
  const [error, setError]                 = useState("");
  const [search, setSearch]               = useState("");
  const [selectedCandidateId, setSelected] = useState(null); // null = list view

  const fetchList = useCallback(() => {
    setLoading(true);
    setError("");
    fetch(`${API_BASE}/onboarding/accepted-candidates`, {
      headers: getAuthHeaders(),
    })
      .then(readJson)
      .then(data => {
        if (!data?.success) {
          setError(data?.message || "Failed to load accepted candidates");
          return;
        }
        setCandidates(Array.isArray(data.data) ? data.data : []);
      })
      .catch(() => setError("Network error while loading candidates"))
      .finally(() => { setLoading(false); setLoaded(true); });
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  // ── Detail view (no route change — just swap local state) ──────────────────
  if (selectedCandidateId) {
    return (
      <AcceptedPageShell standalone={standalone}>
        <CandidateDetailView
          candidateId={selectedCandidateId}
          onBack={() => {
            setSelected(null);
            fetchList(); // refresh counts/progress in case docs changed
          }}
        />
      </AcceptedPageShell>
    );
  }

  // ── List view ────────────────────────────────────────────────────────────
  const q = search.trim().toLowerCase();
  const filtered = candidates.filter(c => !q || [c.candidatename, c.candidateEmail, c.jobRole, c.companyName]
    .some(f => f != null && String(f).toLowerCase().includes(q)));

  const renderBody = () => {
    if (loading && !loaded) return (
      <CardState>
        <CircularProgress size={32} sx={{ color: COLORS.primary }} />
        <Typography sx={{ fontSize: TYPE.body, color: COLORS.muted }}>Loading candidates…</Typography>
      </CardState>
    );
    if (error && candidates.length === 0) return (
      <CardState>
        <Typography sx={{ fontSize: TYPE.body, color: COLORS.error }}>{error}</Typography>
        <Button variant="outlined" onClick={fetchList} disabled={loading} sx={btnSx}>
          {loading ? "Retrying…" : "Retry"}
        </Button>
      </CardState>
    );
    if (filtered.length === 0) return (
      <CardState>
        <Typography sx={{ fontSize: TYPE.body, color: COLORS.muted }}>
          {candidates.length === 0
            ? "No candidates have accepted their offer yet."
            : "No candidates match your search."}
        </Typography>
      </CardState>
    );
    return (
      <Table sx={{ tableLayout: "fixed", width: "100%" }}>
        <colgroup>{COLS.map((c, i) => <col key={i} style={{ width: c.w }} />)}</colgroup>
        <TableHead>
          <TableRow>
            {COLS.map((c, i) => <TableCell key={i} align={c.align || "left"} sx={headSx}>{c.h}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map(c => {
            const statusStyle = STATUS_COLORS[c.onboarding_status] || STATUS_COLORS["Pending Setup"];
            const pct = Math.max(0, Math.min(100, Number(c.onboarding_pct) || 0));
            const roleLine = [c.jobRole, c.companyName].filter(Boolean).join(" · ");
            return (
              <TableRow
                key={c._id}
                hover
                tabIndex={0}
                onClick={() => setSelected(c._id)}
                onKeyDown={e => { if (e.key === "Enter") setSelected(c._id); }}
                sx={{ cursor: "pointer", "&:last-child td": { borderBottom: 0 } }}
              >
                <TableCell sx={cellSx}>
                  <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                      bgcolor: COLORS.accentSoft, color: COLORS.primary, fontWeight: 700, fontSize: TYPE.body,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {(c.candidatename || "?").charAt(0).toUpperCase()}
                    </Box>
                    <Box minWidth={0}>
                      <Typography sx={{ fontWeight: 700, fontSize: TYPE.small, color: COLORS.ink, ...ELLIPSIS }} title={c.candidatename}>
                        {c.candidatename || "—"}
                      </Typography>
                      <Typography sx={{ fontSize: TYPE.caption, color: COLORS.muted, ...ELLIPSIS }} title={c.candidateEmail}>
                        {c.candidateEmail || "—"}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={cellSx}>
                  <Typography sx={{ fontSize: TYPE.small, color: COLORS.ink, ...ELLIPSIS }} title={roleLine}>
                    {roleLine || "—"}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ ...cellSx, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                  {c.document_count ?? 0}
                </TableCell>
                <TableCell sx={cellSx}>
                  <LinearProgress
                    variant="determinate" value={pct}
                    sx={{ height: 6, borderRadius: 3, bgcolor: COLORS.border,
                      "& .MuiLinearProgress-bar": { bgcolor: COLORS.primary, borderRadius: 3 } }}
                  />
                  <Typography sx={{ fontSize: TYPE.caption, color: COLORS.muted, mt: 0.5, fontVariantNumeric: "tabular-nums" }}>
                    {pct}% complete
                  </Typography>
                </TableCell>
                <TableCell sx={cellSx}>
                  <Chip
                    label={humanizeStatus(c.onboarding_status || "Pending Setup")}
                    size="small"
                    sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, maxWidth: "100%" }}
                  />
                </TableCell>
                <TableCell align="right" sx={cellSx}>
                  <ArrowForward sx={{ fontSize: 18, color: COLORS.muted, verticalAlign: "middle" }} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <AcceptedPageShell standalone={standalone}>
      {/* ── Page header ── */}
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap" mb={3}>
        <Box minWidth={0}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{
              width: 36, height: 36, borderRadius: `${RADIUS.md}px`, flexShrink: 0,
              bgcolor: COLORS.successSoft, display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <CheckCircle sx={{ fontSize: 20, color: COLORS.success }} />
            </Box>
            <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.page, color: COLORS.primaryDeep, lineHeight: 1.25 }}>
              Offer Accepted Candidates
            </Typography>
          </Box>
          <Typography sx={{ fontSize: TYPE.body, color: COLORS.muted, mt: 0.5 }}>
            Candidates who have accepted their offer and submitted onboarding documents.
          </Typography>
        </Box>
        <TextField
          size="small"
          placeholder="Search by name, email, role, or company…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ width: 360, maxWidth: "100%", bgcolor: COLORS.surface }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: COLORS.muted }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {error && candidates.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}
          action={<Button size="small" onClick={fetchList} disabled={loading} sx={{ textTransform: "none", fontWeight: 600 }}>Retry</Button>}>
          {error}
        </Alert>
      )}

      <Card elevation={0} sx={{ ...cardSx, overflow: "hidden" }}>
        {renderBody()}
      </Card>
    </AcceptedPageShell>
  );
}
